import React, { Component } from "react";
import PropTypes from "prop-types";

import { noop, hexToRGBA, getStrokeDasharray } from "../../utils";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import ClickableCircle from "../components/ClickableCircle";

class EachHeart extends Component {
  constructor(props) {
    super(props);

    this.handleHover = this.handleHover.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.handleResizeHandleDragStart =
      this.handleResizeHandleDragStart.bind(this);
    this.handleResizeHandleDrag = this.handleResizeHandleDrag.bind(this);

    this.handleRotateHandleDragStart =
      this.handleRotateHandleDragStart.bind(this);
    this.handleRotateHandleDrag = this.handleRotateHandleDrag.bind(this);

    this.isHover = this.isHover.bind(this);
    this.helper = this.helper.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.renderHandles = this.renderHandles.bind(this);

    // Heart shape definition (from ChartIQ)
    this.dimension = [23, 20];

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  helper(props, moreProps) {
    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;
    const { x, y, x2, y2, sx, sy, angle, interactive } = props;

    // Convert data coordinates to pixels
    const centerX = xScale(x);
    const centerY = yScale(y);

    // Use stored scale values
    let scaleX = sx || 1;
    let scaleY = sy || 1;
    let rotationAngle = angle || 0;

    // Only calculate scale from x2/y2 during initial placement (interactive=false means temp preview)
    if (
      interactive === false &&
      x2 !== null &&
      y2 !== null &&
      x2 !== undefined &&
      y2 !== undefined
    ) {
      const x2Pixel = xScale(x2);
      const y2Pixel = yScale(y2);

      const dx = x2Pixel - centerX;
      const dy = y2Pixel - centerY;

      // CRITICAL: Use same formula as resize handle for consistent scaling
      // Since angle is 0 during initial creation, no rotation transform needed
      const baseDim = this.dimension[0];
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Only calculate scale if drag distance is significant (> 5 pixels)
      if (distance > 5) {
        scaleX = Math.max(0.2, distance / (baseDim / 2));
        scaleY = scaleX; // Keep proportional initially
      }
    }

    // Handle positions (in radians for SVG)
    const angleRad = (rotationAngle * Math.PI) / 180;

    // Calculate handle positions (relative to center, after scale)
    const resizeHandleX = (scaleX * this.dimension[0]) / 2;
    const resizeHandleY = (scaleY * this.dimension[1]) / 2;

    const rotateHandleX = (scaleX * this.dimension[0]) / 2;
    const rotateHandleY = 0;

    // Apply rotation to handle positions (in pixels)
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const resizeHandleRotatedX = resizeHandleX * cos - resizeHandleY * sin;
    const resizeHandleRotatedY = resizeHandleX * sin + resizeHandleY * cos;

    const rotateHandleRotatedX = rotateHandleX * cos - rotateHandleY * sin;
    const rotateHandleRotatedY = rotateHandleX * sin + rotateHandleY * cos;

    // Convert handle pixel positions to data coordinates for ClickableCircle
    const resizeHandleDataX = xScale.invert(centerX + resizeHandleRotatedX);
    const resizeHandleDataY = yScale.invert(centerY + resizeHandleRotatedY);

    const rotateHandleDataX = xScale.invert(centerX + rotateHandleRotatedX);
    const rotateHandleDataY = yScale.invert(centerY + rotateHandleRotatedY);

    return {
      x: centerX,
      y: centerY,
      sx: scaleX,
      sy: scaleY,
      angle: rotationAngle,
      angleRad,
      resizeHandleX: centerX + resizeHandleRotatedX,
      resizeHandleY: centerY + resizeHandleRotatedY,
      rotateHandleX: centerX + rotateHandleRotatedX,
      rotateHandleY: centerY + rotateHandleRotatedY,
      resizeHandleDataX,
      resizeHandleDataY,
      rotateHandleDataX,
      rotateHandleDataY,
    };
  }

  handleDragStart() {
    const { x, y, sx, sy, angle } = this.props;
    this.dragStart = { x, y, sx, sy, angle };
  }

  handleDrag(moreProps) {
    const { index, onDrag } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
      startPos,
      mouseXY,
      xAccessor,
      fullData,
    } = moreProps;

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX = xScale.invert(xScale(this.dragStart.x) - dx);
    const newY = yScale.invert(yScale(this.dragStart.y) - dy);

    onDrag(index, {
      x: newX,
      y: newY,
      sx: this.dragStart.sx,
      sy: this.dragStart.sy,
      angle: this.dragStart.angle,
    });
  }

  handleResizeHandleDragStart() {
    const { x, y, sx, sy, angle } = this.props;
    this.resizeStart = { x, y, sx, sy, angle };
  }

  handleResizeHandleDrag(moreProps) {
    const { index, onDrag } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
      mouseXY,
    } = moreProps;

    const centerX = xScale(this.resizeStart.x);
    const centerY = yScale(this.resizeStart.y);

    const angleRad = (this.resizeStart.angle * Math.PI) / 180;

    // Get mouse position relative to center
    const dx = mouseXY[0] - centerX;
    const dy = mouseXY[1] - centerY;

    // Rotate mouse position by inverse of current rotation
    const cos = Math.cos(-angleRad);
    const sin = Math.sin(-angleRad);
    const rotatedDx = dx * cos - dy * sin;
    const rotatedDy = dx * sin + dy * cos;

    // Calculate scale based on rotated distance
    const baseDim = this.dimension[0];
    const newSx = Math.max(0.3, Math.abs(rotatedDx / (baseDim / 2)));
    const newSy = Math.max(0.3, Math.abs(rotatedDy / (baseDim / 2)));

    // Preserve position and angle while resizing
    onDrag(index, {
      x: this.resizeStart.x,
      y: this.resizeStart.y,
      sx: newSx,
      sy: newSy,
      angle: this.resizeStart.angle,
    });
  }

  handleRotateHandleDragStart() {
    const { x, y, sx, sy, angle } = this.props;
    this.rotateStart = { x, y, sx, sy, angle };
  }

  handleRotateHandleDrag(moreProps) {
    const { index, onDrag } = this.props;
    const {
      xScale,
      chartConfig: { yScale },
      mouseXY,
    } = moreProps;

    const centerX = xScale(this.rotateStart.x);
    const centerY = yScale(this.rotateStart.y);

    const dx = mouseXY[0] - centerX;
    const dy = mouseXY[1] - centerY;

    let angleRad = Math.atan2(dy, dx);
    let angleDeg = ((angleRad * 180) / Math.PI + 360) % 360;

    onDrag(index, {
      x: this.rotateStart.x,
      y: this.rotateStart.y,
      sx: this.rotateStart.sx,
      sy: this.rotateStart.sy,
      angle: angleDeg,
    });
  }

  handleDragComplete(...rest) {
    this.dragStart = null;
    this.resizeStart = null;
    this.rotateStart = null;
    this.props.onDragComplete(...rest);
  }

  isHover(moreProps) {
    const { tolerance = 10 } = this.props;
    const {
      mouseXY,
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const { x, y, sx, sy, angleRad } = this.helper(this.props, moreProps);

    // Mouse position
    const [mouseX, mouseY] = mouseXY;

    // Transform mouse to heart's local coordinate system
    const dx = mouseX - x;
    const dy = mouseY - y;

    // Rotate by inverse angle
    const cos = Math.cos(-angleRad);
    const sin = Math.sin(-angleRad);
    const localX = (dx * cos - dy * sin) / sx;
    const localY = (dx * sin + dy * cos) / sy;

    // Check if inside bounding box (with tolerance)
    const halfW = this.dimension[0] / 2 + tolerance / Math.abs(sx);
    const halfH = this.dimension[1] / 2 + tolerance / Math.abs(sy);

    return Math.abs(localX) < halfW && Math.abs(localY) < halfH;
  }

  // Heart path from ChartIQ: dimension [23, 20]
  // Path is centered at (11, 9.5) which is (23-1)/2, (20-1)/2
  getHeartPath() {
    const cx = (this.dimension[0] - 1) / 2;
    const cy = (this.dimension[1] - 1) / 2;

    // ChartIQ Bezier path (converted to SVG format)
    return `
			M ${11 - cx},${3 - cy}
			C ${11 - cx},${2.4 - cy} ${10 - cx},${0 - cy} ${6 - cx},${0 - cy}
			C ${0 - cx},${0 - cy} ${0 - cx},${7.5 - cy} ${0 - cx},${7.5 - cy}
			C ${0 - cx},${11 - cy} ${4 - cx},${15.4 - cy} ${11 - cx},${19 - cy}
			C ${18 - cx},${15.4 - cy} ${22 - cx},${11 - cy} ${22 - cx},${7.5 - cy}
			C ${22 - cx},${7.5 - cy} ${22 - cx},${0 - cy} ${16 - cx},${0 - cy}
			C ${13 - cx},${0 - cy} ${11 - cx},${2.4 - cy} ${11 - cx},${3 - cy}
			Z
		`.trim();
  }

  renderSVG(moreProps) {
    const { stroke, fill } = this.props;
    const { x, y, sx, sy, angleRad } = this.helper(this.props, moreProps);

    const pathD = this.getHeartPath();

    const angleDeg = (angleRad * 180) / Math.PI;

    return (
      <g
        transform={`translate(${x}, ${y}) rotate(${angleDeg}) scale(${sx}, ${sy})`}
      >
        <path
          d={pathD}
          fill={fill.color}
          fillOpacity={fill.opacity}
          stroke={stroke.color}
          strokeWidth={stroke.width / Math.max(sx, sy)} // Adjust stroke for scale
          strokeOpacity={stroke.opacity}
          strokeDasharray={getStrokeDasharray(stroke.dash)}
        />
      </g>
    );
  }

  renderHandles(moreProps) {
    const { selected, interactive } = this.props;
    const { hover } = this.state;

    if (interactive === false || (!selected && !hover)) {
      return null;
    }

    const { resizeHandleX, resizeHandleY, rotateHandleX, rotateHandleY } =
      this.helper(this.props, moreProps);

    return (
      <g>
        {/* Resize handle (corner) - blue */}
        <circle
          cx={resizeHandleX}
          cy={resizeHandleY}
          r={5}
          fill="#2196F3"
          stroke="#FFFFFF"
          strokeWidth={2}
          fillOpacity={0.8}
          strokeOpacity={1}
        />

        {/* Rotate handle (side) - yellow */}
        <circle
          cx={rotateHandleX}
          cy={rotateHandleY}
          r={5}
          fill="#FFC107"
          stroke="#FFFFFF"
          strokeWidth={2}
          fillOpacity={0.8}
          strokeOpacity={1}
        />
      </g>
    );
  }

  getHandleCoords(moreProps) {
    const {
      resizeHandleDataX,
      resizeHandleDataY,
      rotateHandleDataX,
      rotateHandleDataY,
    } = this.helper(this.props, moreProps);
    return {
      resizeHandleDataX,
      resizeHandleDataY,
      rotateHandleDataX,
      rotateHandleDataY,
    };
  }

  render() {
    const { x, y, selected, hoverText, interactive, interactiveCursorClass } =
      this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover } = this.state;
    const cursorClass =
      interactiveCursorClass || "react-stockcharts-move-cursor";

    return (
      <g>
        <GenericChartComponent
          isHover={interactive !== false ? this.isHover : null}
          onHover={interactive !== false ? this.handleHover : null}
          onUnHover={interactive !== false ? this.handleHover : null}
          onDragStart={interactive !== false ? this.handleDragStart : null}
          onDrag={interactive !== false ? this.handleDrag : null}
          onDragComplete={
            interactive !== false ? this.handleDragComplete : null
          }
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          interactiveCursorClass={
            interactive !== false ? cursorClass : undefined
          }
          selected={selected}
          enableDragOnHover={interactive !== false}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        {/* Handles rendered via separate GenericChartComponent (for visual only) */}
        <GenericChartComponent
          svgDraw={this.renderHandles}
          canvasToDraw={getMouseCanvas}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        {/* Center move handle */}
        <ClickableCircle
          show={interactive !== false && (selected || hover)}
          cx={x}
          cy={y}
          r={5}
          fill="#2196F3"
          stroke="#FFFFFF"
          strokeWidth={2}
          fillOpacity={0.8}
          strokeOpacity={1}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDragStart={this.handleDragStart}
          onDrag={this.handleDrag}
          onDragComplete={this.handleDragComplete}
        />

        {/* Resize handle - needs special wrapper to get coords */}
        {interactive !== false && (selected || hover) && (
          <GenericChartComponent
            svgDraw={(moreProps) => {
              const { resizeHandleDataX, resizeHandleDataY } =
                this.getHandleCoords(moreProps);
              return (
                <ClickableCircle
                  show={true}
                  cx={resizeHandleDataX}
                  cy={resizeHandleDataY}
                  r={5}
                  fill="#2196F3"
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  fillOpacity={0.8}
                  strokeOpacity={1}
                  interactiveCursorClass="react-stockcharts-nwse-resize"
                  onDragStart={this.handleResizeHandleDragStart}
                  onDrag={this.handleResizeHandleDrag}
                  onDragComplete={this.handleDragComplete}
                />
              );
            }}
            canvasToDraw={getMouseCanvas}
            drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
          />
        )}

        {/* Rotate handle - needs special wrapper to get coords */}
        {interactive !== false && (selected || hover) && (
          <GenericChartComponent
            svgDraw={(moreProps) => {
              const { rotateHandleDataX, rotateHandleDataY } =
                this.getHandleCoords(moreProps);
              return (
                <ClickableCircle
                  show={true}
                  cx={rotateHandleDataX}
                  cy={rotateHandleDataY}
                  r={5}
                  fill="#FFC107"
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  fillOpacity={0.8}
                  strokeOpacity={1}
                  interactiveCursorClass="react-stockcharts-ew-resize"
                  onDragStart={this.handleRotateHandleDragStart}
                  onDrag={this.handleRotateHandleDrag}
                  onDragComplete={this.handleDragComplete}
                />
              );
            }}
            canvasToDraw={getMouseCanvas}
            drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
          />
        )}

        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

EachHeart.propTypes = {
  index: PropTypes.number,

  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  x2: PropTypes.number,
  y2: PropTypes.number,
  sx: PropTypes.number,
  sy: PropTypes.number,
  angle: PropTypes.number,

  stroke: PropTypes.shape({
    color: PropTypes.string,
    dash: PropTypes.string,
    width: PropTypes.number,
    opacity: PropTypes.number,
  }).isRequired,

  fill: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
  }).isRequired,

  selected: PropTypes.bool,
  interactive: PropTypes.bool,
  interactiveCursorClass: PropTypes.string,
  hoverText: PropTypes.object.isRequired,

  onDrag: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
};

EachHeart.defaultProps = {
  onDrag: noop,
  onDragComplete: noop,
  selected: false,
  sx: 1,
  sy: 1,
  angle: 0,
  hoverText: {
    enable: false,
  },
};

export default EachHeart;
